import {TranslationKey} from './TranslationKeyTypes'

export const DEFAULT_LOCALE = 'en' as const

// A new locale also needs its measurement area patterns: `bun run generate:measurement-area-patterns` in services/app.
export const locales = [
  DEFAULT_LOCALE,
  'da',
  'de',
  'es',
  'fr',
  'id-ID',
  'it',
  'ja',
  'ko',
  'ms',
  'nb',
  'nl',
  'pt',
  'ru',
  'sv-SE',
  'th',
  'tr',
  'uk-UA',
  'vi',
  'zh-Hans',
  'zh-Hant'
] as const
export type LanguageCode = (typeof locales)[number]
export const localeSet = new Set<string>(locales)

export function isLanguageCode(code?: string): code is LanguageCode {
  if (typeof code !== 'string') {
    return false
  }

  return localeSet.has(code)
}

export function localesToLanguageCode(locales: readonly string[]) {
  for (const locale of locales) {
    if (isLanguageCode(locale)) {
      return locale
    }
  }

  return 'en'
}

export function trimCountryCode(language: LanguageCode) {
  return language.split('-')[0]!
}

export function maybeFindValidLanguageCode(locales?: readonly string[]): LanguageCode | undefined {
  return locales ? locales.find((l) => isLanguageCode(l)) : undefined
}

export function langToTranslationKey(language?: LanguageCode): TranslationKey {
  return language ? (`language-${language}` as TranslationKey) : 'doesNotApply'
}

const languageCodeByLowercaseTag = new Map<string, LanguageCode>(locales.map((code) => [code.toLowerCase(), code]))

// Browsers send a bare base tag (`sv`) where our catalog carries a regioned code (`sv-SE`).
// Derived from `locales` so a regioned locale added later matches without a second edit here.
const languageCodeByBaseTag = new Map<string, LanguageCode>(locales.map((code) => [code.split('-')[0] ?? code, code]))
// The Norwegian macrolanguage tag has no regioned code to derive from; our catalog carries Bokmål.
languageCodeByBaseTag.set('no', 'nb')

// Falls back from an unsupported region variant to its base language (fr-CA → fr).
export function languageCodeForTag(tag: string): LanguageCode | undefined {
  const lowercaseTag = tag.toLowerCase()
  const exactMatch = languageCodeByLowercaseTag.get(lowercaseTag)
  if (exactMatch) {
    return exactMatch
  }
  const subtags = lowercaseTag.split('-')
  const [baseTag = ''] = subtags
  if (baseTag === 'zh') {
    // Chinese tags carry a script or region rather than our codes; the Traditional-script
    // regions (TW, HK, MO) map to zh-Hant, every other Chinese tag to zh-Hans.
    const usesTraditional = subtags.some((subtag) => ['hant', 'tw', 'hk', 'mo'].includes(subtag))
    return usesTraditional ? 'zh-Hant' : 'zh-Hans'
  }
  return languageCodeByLowercaseTag.get(baseTag) ?? languageCodeByBaseTag.get(baseTag)
}

/**
 * Picks the best supported locale from an HTTP Accept-Language header value: honors the
 * header's quality weights, and falls back from an unsupported region variant to its base
 * language (fr-CA → fr). Returns undefined when no tag matches a supported locale.
 */
export function acceptLanguageToLanguageCode(header: string | null | undefined): LanguageCode | undefined {
  if (!header) {
    return undefined
  }
  const weightedTags = header
    .split(',')
    .map((entry) => {
      const [tag = '', ...params] = entry.trim().split(';')
      // Parameter names are case-insensitive, so a `Q=` weight must not fall through to the
      // default weight of 1 and outrank a tag the client actually prefers.
      const qualityParam = params.map((param) => param.trim().toLowerCase()).find((param) => param.startsWith('q='))
      const parsedQuality = qualityParam ? Number.parseFloat(qualityParam.slice(2)) : 1
      return {tag: tag.trim(), quality: Number.isFinite(parsedQuality) ? parsedQuality : 0}
    })
    .filter(({tag, quality}) => tag.length > 0 && tag !== '*' && quality > 0)
    .sort((a, b) => b.quality - a.quality)

  for (const {tag} of weightedTags) {
    const languageCode = languageCodeForTag(tag)
    if (languageCode) {
      return languageCode
    }
  }
  return undefined
}

/**
 * Maps OAuth locale codes to our supported locale format.
 * Handles common OAuth provider locale variations that don't match our exact locale codes.
 */
export function normalizeOAuthLocale(oauthLocale: string): LanguageCode {
  // OAuth locale mapping for known problematic cases
  const oauthLocaleMap: Record<string, LanguageCode> = {
    id: 'id-ID', // Indonesian
    'ko-Kore': 'ko', // Korean
    sv: 'sv-SE', // Swedish
    uk: 'uk-UA', // Ukrainian
    zh: 'zh-Hans' // Chinese (default to simplified)
  }

  // First check if it's a direct mapping case
  if (oauthLocaleMap[oauthLocale]) {
    return oauthLocaleMap[oauthLocale]
  }

  // If it's already a valid locale code, return it
  if (isLanguageCode(oauthLocale)) {
    return oauthLocale
  }

  // Default to English if no mapping found
  return DEFAULT_LOCALE
}

export type TranslationSet = Partial<Record<TranslationKey, Partial<Record<LanguageCode, string>>>>
